import { FC } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Divider,
  Avatar,
} from '@mui/material';
import { Phone, Email } from '@mui/icons-material';
import { useUsersState } from '../../contexts/users.context';
import { UserByCategoryItem } from 'src/apis/users/users.api.interfaces';
import {
  getStatusColor,
  getStatusLabel,
  getInitials,
  getFullName,
  formatDate,
  formatDateTime,
  CATEGORY_DISPLAY_LABELS,
} from '../../users.helpers';
import { UserStatus, UserCategory } from '../../users.interfaces';
import { useTranslation } from 'src/hooks/useTranslationHook';

/**
 * Unified detail panel for a user selected from any by-category tab. Renders the
 * shared `UserByCategoryItem` fields, since every category now returns the same
 * flat DTO (see `UserByCategoryDto`).
 */
const CategoryUserDetailsView: FC = () => {
  const { translateText } = useTranslation();
  const { selectedUser } = useUsersState();

  if (!selectedUser) {
    return (
      <Box data-testid="category-user-details-empty">
        <Typography>{translateText('users.details.noUserSelected')}</Typography>
      </Box>
    );
  }

  const user = selectedUser as unknown as UserByCategoryItem;
  const nA = translateText('users.details.nA');

  const roleLabel =
    translateText(`users.options.roles.${user.userRole}`) || user.userRole;
  const categoryLabel =
    CATEGORY_DISPLAY_LABELS[user.userCategory as UserCategory] ??
    user.userCategory;
  const genderLabel = user.gender
    ? translateText(`users.options.gender.${user.gender}`) || user.gender
    : nA;

  const renderField = (
    label: string,
    value?: string,
    testId?: string
  ): JSX.Element => (
    <Grid item xs={12} sm={6} md={3} data-testid={testId}>
      <Typography
        variant="caption"
        color="textSecondary"
        sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
      >
        {label}
      </Typography>
      <Typography
        variant="body1"
        fontWeight="medium"
        sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
      >
        {value || nA}
      </Typography>
    </Grid>
  );

  return (
    <Box data-testid="category-user-details-container">
      <Card variant="outlined">
        <CardContent
          sx={{
            p: { xs: 1.5, sm: 2 },
            '&:last-child': { pb: { xs: 1.5, sm: 2 } },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              mb: { xs: 1.5, sm: 2 },
              gap: { xs: 1, sm: 2 },
            }}
            data-testid="category-user-details-header"
          >
            <Avatar
              src={user.profilePicture}
              sx={{
                mr: { xs: 0, sm: 2 },
                mb: { xs: 1, sm: 0 },
                bgcolor: 'primary.main',
                width: { xs: 56, sm: 64 },
                height: { xs: 56, sm: 64 },
              }}
              data-testid="category-user-details-avatar"
            >
              {getInitials(user.firstName, user.lastName)}
            </Avatar>
            <Box>
              <Typography
                variant="h5"
                fontWeight="bold"
                data-testid="category-user-details-name"
                sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' } }}
              >
                {getFullName(user.firstName, user.lastName)}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                <Chip
                  label={roleLabel}
                  size="small"
                  color="primary"
                  variant="outlined"
                  data-testid="category-user-details-role"
                />
                {user.userStatus && (
                  <Chip
                    label={getStatusLabel(user.userStatus as UserStatus)}
                    size="small"
                    sx={{
                      backgroundColor: getStatusColor(
                        user.userStatus as UserStatus
                      ),
                      color: 'white',
                      fontWeight: 'medium',
                    }}
                    data-testid="category-user-details-status"
                  />
                )}
              </Box>
            </Box>
          </Box>

          <Divider sx={{ mb: { xs: 1.5, sm: 2 } }} />
          <Typography
            variant="h6"
            fontWeight="bold"
            sx={{
              mb: { xs: 1, sm: 1.5 },
              fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
            }}
            data-testid="category-user-details-title"
          >
            {translateText('users.details.basicInformation')}
          </Typography>
          <Grid container spacing={{ xs: 1.5, sm: 2 }}>
            {renderField(
              translateText('users.details.category'),
              categoryLabel,
              'detail-category'
            )}
            {renderField(
              translateText('users.details.gender'),
              genderLabel,
              'detail-gender'
            )}
            {renderField(
              translateText('users.details.dateOfBirth'),
              user.dateOfBirth ? formatDate(user.dateOfBirth) : undefined,
              'detail-date-of-birth'
            )}
            {renderField(
              translateText('users.details.userName'),
              user.userName,
              'detail-username'
            )}
            {renderField(
              translateText('users.details.userId'),
              user.id,
              'detail-user-id'
            )}

            <Grid item xs={12}>
              <Divider sx={{ my: { xs: 1, sm: 1.5 } }} />
              <Typography
                variant="h6"
                fontWeight="bold"
                sx={{
                  mb: { xs: 1, sm: 1.5 },
                  fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
                }}
                data-testid="contact-section-title"
              >
                {translateText('users.details.contactInformation')}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3} data-testid="detail-email">
              <Typography variant="caption" color="textSecondary">
                {translateText('users.details.email')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                <Email sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
                <Typography variant="body1" fontWeight="medium">
                  {user.email || nA}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3} data-testid="detail-phone">
              <Typography variant="caption" color="textSecondary">
                {translateText('users.details.phone')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                <Phone sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
                <Typography variant="body1" fontWeight="medium">
                  {user.phone || nA}
                </Typography>
              </Box>
            </Grid>

            {user.description && (
              <Grid item xs={12} data-testid="detail-description">
                <Typography variant="caption" color="textSecondary">
                  {translateText('users.details.description')}
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {user.description}
                </Typography>
              </Grid>
            )}

            <Grid item xs={12}>
              <Divider sx={{ my: { xs: 1, sm: 1.5 } }} />
              <Typography
                variant="h6"
                fontWeight="bold"
                sx={{
                  mb: { xs: 1, sm: 1.5 },
                  fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
                }}
                data-testid="account-section-title"
              >
                {translateText('users.details.accountInformation')}
              </Typography>
            </Grid>
            {renderField(
              translateText('users.details.createdDate'),
              user.createdAt ? formatDateTime(user.createdAt) : undefined,
              'detail-created-date'
            )}
            {renderField(
              translateText('users.details.updatedDate'),
              user.updatedAt ? formatDateTime(user.updatedAt) : undefined,
              'detail-updated-date'
            )}
            {renderField(
              translateText('users.details.lastActive'),
              user.lastActive ? formatDateTime(user.lastActive) : undefined,
              'detail-last-active'
            )}
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default CategoryUserDetailsView;
